// React
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
// Material components
import { withStyles } from '@mui/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import ExportIcon from '@mui/icons-material/SaveAlt';
import EditIcon from '@mui/icons-material/Edit';
import CloneIcon from '@mui/icons-material/FileCopy';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import Typography from '@mui/material/Typography';
import { Icon, Switch } from '@mui/material';
// PCP Components
import OnMousePopover from 'components/commons/OnMousePopover';
import DialogUsers from 'components/dialogs/account/DialogUsers';
import DialogAccountModules from 'components/dialogs/account/DialogAccountModules';
// Style
import { useStyles } from 'components/rows/AccountRow.style';
// Theme
import theme from 'assets/theme/theme.js';
// Images
import imgNotFound from 'assets/img/img-not-found.png';
// Actions
import AccountAction from 'actions/AccountAction';
// Svg icons
import marketplaceIcon from 'assets/svg/marketplaceIcon';
import TrendIcon from 'assets/svg/TrendIcon';
import PricingEngineIcon from 'assets/svg/PricingEngineIcon';
// Locales
import translate from 'locales/i18n';

const modules = [{
    name: 'spe',
    icon: 'euro_symbol'
}, {
    name: 'perf',
    icon: 'trending_up'
}, {
    name: 'adbox',
    icon: 'mail'
}, {
    name: 'slider',
    icon: 'calendar_view_day'
}, {
    name: 'pricingEngine',
    iconComponent: PricingEngineIcon
}, {
    name: 'mp',
    iconComponent: marketplaceIcon
},
{
    name: 'trends',
    iconComponent: TrendIcon
}];

const ActiveSwitch = withStyles({
    switchBase: {
        color: theme.palette.red.main,
        '&$checked': {
            color: theme.palette.primary.main
        },
        '&$checked + $track': {
            backgroundColor: theme.palette.primary.main
        },
    },
    checked: {},
    track: {},
})(Switch);

const AccountRow = ({ account, cloneAccount, editAccount, exportAccount, index, switchActiveAccount }) => {
    const [users, setUsers] = useState([]);
    const [dialogUsersOpen, setDialogUsersOpen] = useState(false);
    const [dialogAccountModulesOpen, setDialogAccountModulesOpen] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const classes = useStyles();

    const openDialogUsers = () => {
        setDialogUsersOpen(true);
        AccountAction.getAccountUsers(account.id).then(result => {
            setUsers(result);
            setIsLoadingUsers(false);
        });
    };

    const renderDialogAccountModules = () => <DialogAccountModules open={dialogAccountModulesOpen}
        close={() => setDialogAccountModulesOpen(false)}
        account={account} />;

    const renderDialogUsers = () => <DialogUsers users={users}
        open={dialogUsersOpen}
        close={() => {
            setDialogUsersOpen(false);
            setIsLoadingUsers(false);
        }}
        isLoadingUsers={isLoadingUsers} />;

    return (
        <TableRow key={account.id} style={{ backgroundColor: index % 2 === 0 ? theme.palette.grey.semilight : theme.palette.white.main }}>
            <TableCell className={classes.cell} align='left'>
                <div className={classes.nameCellContent}>
                    <img alt={account.name} src={account.logoUrl} width={30} style={{ marginRight: '5px', marginLeft: '5px' }}
                        onError={(error) => {
                            error.target.onerror = null;
                            error.target.src = imgNotFound;
                        }}></img>
                    {account.name}
                </div>
            </TableCell>
            <TableCell className={classes.cell} align='center'>
                <div>
                    {account.users ? account.users.length < 2 ? account.users[0] ? <OnMousePopover onClick={openDialogUsers} text={<PersonIcon className={classes.userIcon} />}>{account.users[0]}</OnMousePopover> : '-' : <OnMousePopover onClick={openDialogUsers} text={<GroupIcon className={classes.userIcon} />}>{account.users.map(u => <Typography key={u}>{u}</Typography>)}</OnMousePopover> : '-'}
                    {renderDialogUsers()}
                </div>
            </TableCell>
            <TableCell className={classes.cell} align='center'>{account.type}</TableCell>
            <TableCell className={classes.cell} align='center'>
                <div className={classes.accountModulesCellContent} onClick={() => setDialogAccountModulesOpen(true)}>{modules.map((m, i) => {
                    if (account.modules[m.name]) {
                        if (m.iconComponent) return <div key={i} style={{ marginLeft: '2px', marginTop: '2px' }}> {m.iconComponent('21', '19', theme.palette.primary.main)} </div>;
                        return <Icon color='primary' style={{ marginLeft: '4px', fontSize: '20px' }} key={i}>{m.icon}</Icon>;
                    }
                    return null;
                })}
                </div>
                {renderDialogAccountModules()}</TableCell>
            <TableCell className={classes.cell} align='center' style={{ width: '60px' }}>
                {account.type === 'RETAILER' ?
                    account.site ?
                        account.site.name
                        :
                        'No site found'
                    :
                    '-'
                }
            </TableCell>
            <TableCell className={classes.cell} align='center'>
                <ActiveSwitch
                    checked={account.isActive}
                    onChange={switchActiveAccount}
                    name='Account Active' />
            </TableCell>
            <TableCell className={classes.cell} align='center' style={{ width: '60px' }} ><IconButton title='Edit Account' onClick={editAccount}>
                <EditIcon fontSize='small' />
            </IconButton></TableCell>
            <TableCell className={classes.cell} align='center' style={{ width: '60px' }}><IconButton title='Clone Account' onClick={cloneAccount}>
                <CloneIcon fontSize='small' />
            </IconButton></TableCell>
            <TableCell className={classes.cell} align='center' style={{ width: '60px' }}><IconButton title={translate.i18n('EXPORT_DATA')} onClick={exportAccount}>
                <ExportIcon fontSize='small' />
            </IconButton></TableCell>
            <TableCell className={classes.cell} align='center' style={{ width: '60px' }}><Link title='Import Account' to={'/account/import?account=' + account.id} style={{ marginRight: '12px' }}>
                <ExportIcon style={{ transform: 'rotate(180deg)', color: 'rgba(0, 0, 0, 0.54)' }} fontSize='small' />
            </Link></TableCell>
        </TableRow>
    );
};

AccountRow.propTypes = {
    account: PropTypes.object,
    cloneAccount: PropTypes.func,
    editAccount: PropTypes.func,
    exportAccount: PropTypes.func,
    index: PropTypes.number,
    switchActiveAccount: PropTypes.func,
};

export default AccountRow;
